import React, { useState, useRef, useMemo } from 'react'
import { Field as FormikField } from 'formik'

import MultiSelect from '../../_library/MultiSelect'
import TagsField2 from '../../_library/TagsField2'

import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useVirtual } from 'react-virtual'
import { SelectField, SimpleField } from '../../formik/Fields'

export const GuestTicketsTable = ({
  event,
  data,
  values,
  errors,
  touched,
  handleChange,
  optionsTicketTypeNames,
  optionsAddons,
  handleMultiSelect,
  handleRecipientChangeTags,
  deleteRecipient,
  hasAttendeeTags,
}) => {
  const tableContainerRef = useRef(null)
  const [columnVisibility, setColumnVisibility] = useState({})

  const simpleFieldFn = useMemo(
    () => (key, index, type) =>
      (
        <FormikField
          name={`recipients[${index}].${key}`}
          onChange={handleChange}
          component={SimpleField}
          type={type || 'text'}
        />
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values],
  )

  const selectFieldFn = useMemo(
    () => (key, index, options) =>
      (
        <FormikField
          name={`recipients[${index}].${key}`}
          onChange={handleChange}
          component={SelectField}
          options={options}
        />
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values],
  )

  const multiSelectFieldFn = useMemo(
    () => (key, index, options) =>
      (
        <FormikField
          name={`recipients[${index}].${key}`}
          selectedValues={values.recipients[index].ticketAddOns}
          onChange={handleChange}
          component={MultiSelect}
          options={options}
          onSelect={handleMultiSelect}
          onRemove={handleMultiSelect}
          avoidHighlightFirstOption="true"
          displayValue="name"
          hidePlaceholder="true"
          emptyRecordMsg="No more add-ons available"
        />
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values],
  )

  const columns = useMemo(
    () =>
      [
        {
          accessorKey: 'first_name',
          header: () => <span>First Name</span>,
          cell: info => {
            const { index } = info.row
            return simpleFieldFn('first_name', index)
          },
        },
        {
          accessorKey: 'last_name',
          header: () => <span>Last Name</span>,
          cell: info => {
            const { index } = info.row
            return simpleFieldFn('last_name', index)
          },
        },
        {
          accessorKey: 'email',
          header: () => <span>Email</span>,
          cell: info => {
            const { index } = info.row
            return simpleFieldFn('email', index, 'email')
          },
        },
        {
          accessorKey: 'quantity',
          header: () => <span>Number of Tickets</span>,
          cell: info => {
            const { index } = info.row
            return simpleFieldFn('quantity', index, 'number')
          },
        },
        {
          accessorKey: 'ticketTypeName',
          header: () => <span>Type of Ticket</span>,
          cell: info => {
            const { index } = info.row
            return selectFieldFn('ticketTypeName', index, optionsTicketTypeNames)
          },
        },
        {
          accessorKey: 'ticketAddOns',
          header: () => <span>Attach Add-Ons</span>,
          cell: info => {
            const { index } = info.row
            return multiSelectFieldFn('ticketAddOns', index, optionsAddons)
          },
        },
        {
          accessorKey: 'notes',
          header: () => <span>Notes</span>,
          cell: info => {
            const { index } = info.row
            return simpleFieldFn('notes', index)
          },
        },
        hasAttendeeTags && {
          accessorKey: 'tags',
          header: () => <span>Tags</span>,
          cell: info => {
            const { index } = info.row
            return (
              <TagsField2
                label="Attendee Tags"
                id={'recipients[' + index + '].tags'}
                value={values.recipients[index].tags}
                tags={event.attendeeTags}
                onChange={() => handleRecipientChangeTags(index)}
              />
            )
          },
        },
        {
          size: 30,
          accessorKey: 'delete',
          header: () => <span />,
          cell: info => {
            const { index } = info.row
            return (
              <div aria-hidden className="delete-recipient" onClick={() => deleteRecipient(index)}>
                <i className="fa fa-trash" aria-hidden="true" />
              </div>
            )
          },
        },
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    debugTable: true,
    state: {
      columnVisibility,
    },
  })

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: 5,
    estimateSize: React.useCallback(() => 62, []),
  })
  const { virtualItems: virtualRows, totalSize } = rowVirtualizer
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0

  return (
    <>
      {errors.maxInventory && (
        <div className="relative-error" style={{ marginBottom: 15 }}>
          {errors.maxInventory}
        </div>
      )}
      <div
        ref={tableContainerRef}
        style={{ height: 500, overflow: 'auto' }}
        className="recipients-table-container"
      >
        <table style={{ tableLayout: 'fixed', width: '100%' }} className="table recipients-table">
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{ width: header.getSize(), padding: '8px 20px' }}
                  >
                    <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {virtualRows.map(virtualRow => {
              const row = rows[virtualRow.index]
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td style={{ padding: 6 }} key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              )
            })}
            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: `${paddingBottom}px` }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}
